import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { WELCOME_SLIDES } from './onboardingConfig';

export function WelcomeModal({ open, role, name, onClose, onStartTour }) {
  const slides = WELCOME_SLIDES[role] || WELCOME_SLIDES.creator;
  const [i, setI] = useState(0);
  const slide = slides[i];
  const last = i === slides.length - 1;
  const Icon = slide.icon;

  const next = () => (last ? finish(false) : setI(i + 1));
  const finish = (startTour) => {
    setI(0);
    onClose();
    if (startTour && onStartTour) onStartTour();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) finish(false); }}>
      <DialogContent
        className="sm:max-w-md p-0 overflow-hidden border-0 gap-0"
        data-testid="welcome-modal"
      >
        <DialogTitle className="sr-only">Welcome to Mylikeness</DialogTitle>
        <DialogDescription className="sr-only">A quick introduction to how Mylikeness works.</DialogDescription>
        <div className={`bg-gradient-to-br ${slide.accent} px-7 pt-9 pb-7 text-white relative`}>
          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.25 }}
            >
              <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center mb-5">
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h2 className="text-2xl font-semibold tracking-tight" data-testid="welcome-title">
                {i === 0 && name ? `${slide.title}, ${name.split(' ')[0]}` : slide.title}
              </h2>
              <p className="text-white/85 text-sm mt-2 leading-relaxed">{slide.body}</p>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="px-7 py-5 bg-white">
          <div className="flex items-center justify-center gap-1.5 mb-5">
            {slides.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setI(idx)}
                aria-label={`Slide ${idx + 1}`}
                className={`h-1.5 rounded-full transition-all ${idx === i ? 'w-6 bg-slate-800' : 'w-1.5 bg-slate-300'}`}
              />
            ))}
          </div>

          <div className="flex items-center justify-between">
            {i > 0 ? (
              <Button variant="ghost" size="sm" onClick={() => setI(i - 1)}
                className="text-slate-500" data-testid="welcome-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
            ) : (
              <button onClick={() => finish(false)} className="text-xs text-slate-400 hover:text-slate-600"
                data-testid="welcome-skip-btn">
                Skip intro
              </button>
            )}

            {last ? (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => finish(false)}
                  data-testid="welcome-done-btn">
                  <Check className="w-4 h-4 mr-1" /> Got it
                </Button>
                <Button size="sm" onClick={() => finish(true)}
                  className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="welcome-tour-btn">
                  Take a quick tour <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={next}
                className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="welcome-next-btn">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
